import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, HelpingHand, Users, Send, Flag, MessageCircle, Sparkles, Plus, ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { listCommunityPosts, listCommunityTopics, createCommunityPost, reactToPost, reportPost, getCommunityPost, listComments, addComment } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-card border border-border text-foreground/70 hover:border-primary/40 hover:text-foreground"}`}
    >
      {children}
    </button>
  );
}

const REACTIONS = [
  { key: "hearts",  Icon: Heart,        label: "Heart",  color: "text-primary" },
  { key: "support", Icon: HelpingHand,  label: "Support",color: "text-aquamarine" },
  { key: "same",    Icon: Users,        label: "Same",   color: "text-foreground" },
];

function NewPostSheet({ open, onClose, topics, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [topic, setTopic] = useState("Cycle");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (body.trim().length < 6) { toast.error("Please share a few words more."); return; }
    setBusy(true);
    try {
      const p = await createCommunityPost({ title, body, topic, anonymous: true });
      toast.success("Posted anonymously · thank you for sharing.");
      setTitle(""); setBody("");
      onCreated?.(p);
      onClose?.();
    } catch (e) {
      toast.error(e?.message || "Couldn't post");
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent side="bottom" className="rounded-t-[28px] border-0 bg-card max-h-[88vh] overflow-y-auto" data-testid="new-post-sheet">
        <SheetHeader className="text-left pb-2">
          <div className="inline-flex w-fit items-center gap-1.5 bg-primary/10 text-primary rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider mb-2">
            <MessageCircle size={11} /> Share with community
          </div>
          <SheetTitle className="text-[24px] font-extrabold tracking-tight">Ask anything, anonymously.</SheetTitle>
          <SheetDescription className="text-[12.5px] text-muted-foreground font-medium">
            Your post appears without your name. No personal data is shared.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-3 space-y-3">
          <Select value={topic} onValueChange={setTopic}>
            <SelectTrigger className="h-11 rounded-2xl" data-testid="new-post-topic">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {topics.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="h-11 rounded-2xl" data-testid="new-post-title" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="What's on your mind today? Knya's community holds space." className="min-h-[140px] rounded-2xl resize-none" data-testid="new-post-body" />
          <Button onClick={submit} disabled={busy} className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold" data-testid="new-post-submit">
            <Send size={14} className="mr-1.5" /> {busy ? "Posting…" : "Post anonymously"}
          </Button>
          <p className="text-[11px] text-center text-muted-foreground">
            <Sparkles size={10} className="inline mr-1" /> Posts are gently moderated. Self-harm content is blocked · please reach out to a trusted person.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function CommunityPage() {
  const navigate = useNavigate();
  const { postId } = useParams();
  const [posts, setPosts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [activePost, setActivePost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    listCommunityPosts(topic).then(setPosts).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, [topic]);
  useEffect(() => { listCommunityTopics().then(setTopics).catch(() => {}); }, []);
  useEffect(() => {
    if (postId) {
      getCommunityPost(postId).then(setActivePost).catch(() => {});
      listComments(postId).then(setComments).catch(() => {});
    } else {
      setActivePost(null); setComments([]);
    }
  }, [postId]);

  const react = async (id, kind) => {
    try {
      const r = await reactToPost(id, kind);
      if (activePost?.id === id) setActivePost(r.post);
      setPosts((arr) => arr.map((p) => (p.id === id ? r.post : p)));
    } catch (e) { toast.error(e?.message || "Couldn't react"); }
  };
  const sendComment = async () => {
    if (commentBody.trim().length < 2) return;
    try {
      const c = await addComment(postId, commentBody.trim());
      setComments((arr) => [...arr, c]);
      setCommentBody("");
      // bump count
      setActivePost((p) => p ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p);
    } catch (e) { toast.error(e?.message || "Couldn't comment"); }
  };

  if (activePost) {
    return (
      <div className="kanya-page-pad-bottom" data-testid="community-post-page">
        <div className="px-4 pt-4">
          <button onClick={() => navigate("/community")} className="inline-flex items-center gap-1.5 text-[12px] font-bold text-muted-foreground hover:text-foreground" data-testid="post-back">
            <ArrowLeft size={14} /> Community
          </button>
        </div>
        <article className="px-5 pt-3">
          <p className="text-[10px] uppercase tracking-wider text-primary font-extrabold">{activePost.topic}</p>
          {activePost.title && <h1 className="mt-2 text-[24px] leading-tight font-extrabold tracking-tight">{activePost.title}</h1>}
          <p className="mt-3 text-[15px] leading-relaxed text-foreground/85 whitespace-pre-line font-medium" data-testid="post-body">{activePost.body}</p>
          <p className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground font-extrabold">Anonymous · {fmtDate(activePost.created_at, "MMM d, p")}</p>
          <div className="mt-3 flex items-center gap-1.5">
            {REACTIONS.map(({ key, Icon, label }) => (
              <button key={key} onClick={() => react(activePost.id, key)} className="inline-flex items-center gap-1 rounded-full bg-card border border-border px-2.5 py-1 text-[11px] font-bold hover:border-primary/40" data-testid={`post-react-${key}`}>
                <Icon size={11} /> {activePost.reactions?.[key] || 0}
              </button>
            ))}
            <button onClick={() => { reportPost(activePost.id).then(() => toast.success("Reported")); }} className="ml-auto text-muted-foreground hover:text-destructive" data-testid="post-report"><Flag size={13} /></button>
          </div>

          <div className="mt-6">
            <p className="text-[10px] uppercase tracking-wider text-primary font-extrabold mb-2">{comments.length} reply</p>
            <div className="space-y-2" data-testid="post-comments">
              {comments.map((c) => (
                <div key={c.id} className="rounded-2xl border border-border bg-card p-3">
                  <p className="text-[13px] text-foreground/90 font-medium leading-relaxed">{c.body}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground font-extrabold">Anonymous · {fmtDate(c.created_at, "MMM d, p")}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Input value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Add a kind reply…" className="h-11 rounded-full" onKeyDown={(e) => e.key === "Enter" && sendComment()} data-testid="comment-input" />
              <Button onClick={sendComment} className="h-11 rounded-full bg-primary text-primary-foreground" data-testid="comment-send"><Send size={14} /></Button>
            </div>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="kanya-page-pad-bottom" data-testid="community-page">
      <PageHeader eyebrow="Community" title="You're not alone." subtitle="Anonymous space to ask, share, and feel heard." />

      <div className="px-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="community-topics">
        <Chip active={!topic} onClick={() => setTopic("")} testid="topic-all">All</Chip>
        {topics.map((t) => <Chip key={t} active={topic === t} onClick={() => setTopic(t)} testid={`topic-${t.toLowerCase()}`}>{t}</Chip>)}
      </div>

      <div className="px-4 mt-3 space-y-2.5" data-testid="community-feed">
        {loading && <div className="shimmer h-20 rounded-2xl" />}
        {!loading && posts.length === 0 && (
          <div className="rounded-2xl border border-border bg-muted/30 p-6 text-center">
            <MessageCircle size={20} className="mx-auto text-muted-foreground" />
            <p className="text-[13px] text-muted-foreground mt-2 font-medium">No posts yet. Start the first thread — it's anonymous.</p>
          </div>
        )}
        {!loading && posts.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
            <button
              onClick={() => navigate(`/community/${p.id}`)}
              className="w-full text-left rounded-[20px] bg-card border border-border p-4 hover:border-primary/40 transition-colors"
              data-testid={`post-card-${p.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-wider text-primary font-extrabold">{p.topic}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-extrabold">{fmtDate(p.created_at, "MMM d")}</p>
              </div>
              {p.title && <h3 className="mt-2 text-[16px] leading-tight font-extrabold tracking-tight">{p.title}</h3>}
              <p className="mt-1.5 text-[13px] text-foreground/85 font-medium line-clamp-3 leading-relaxed">{p.body}</p>
              <div className="mt-3 flex items-center gap-3 text-[11px] font-extrabold text-foreground/60">
                <span className="inline-flex items-center gap-1"><Heart size={12} /> {p.reactions?.hearts || 0}</span>
                <span className="inline-flex items-center gap-1"><HelpingHand size={12} /> {p.reactions?.support || 0}</span>
                <span className="inline-flex items-center gap-1"><Users size={12} /> {p.reactions?.same || 0}</span>
                <span className="ml-auto inline-flex items-center gap-1"><MessageCircle size={12} /> {p.comment_count || 0}</span>
              </div>
            </button>
          </motion.div>
        ))}
      </div>

      {/* Floating compose button */}
      <button
        onClick={() => setComposeOpen(true)}
        className="fixed bottom-24 right-4 z-30 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-soft flex items-center justify-center hover:scale-105 transition-transform"
        data-testid="community-compose-fab"
        aria-label="New post"
      >
        <Plus size={22} strokeWidth={2.6} />
      </button>

      <NewPostSheet open={composeOpen} onClose={() => setComposeOpen(false)} topics={topics} onCreated={refresh} />
    </div>
  );
}
